import { createContext, useContext, type ReactNode } from 'react';

import type { EventDetail, EventOpportunity } from '../lib/catalog';

/**
 * The data a block is allowed to see.
 *
 * ── BLOCKS DO NOT FETCH, AND THEY DO NOT REACH ───────────────────────────────────────
 *
 * The host loads the entity once and publishes it here; a block reads the slices its
 * manifest declared it needs. There is no global store to reach into, no hook that
 * fetches by id, and no prop through which a document can name a different entity to
 * load. A document decides WHICH blocks appear and how they look — never what data the
 * page is about.
 *
 * That last point is the security one. If a block could take an `eventId` prop and fetch
 * it, a template would become a way to render one tenant's event inside another's page.
 * The context is the host's entity, always, and the only entity.
 */
export interface EventContextValue {
  kind: 'event';
  event: EventDetail;
  /** Supplementary and allowed to be absent; the engine withholds figures by approval. */
  opportunity: EventOpportunity | null;
}

export type CmsContextValue = EventContextValue;

const CmsContext = createContext<CmsContextValue | null>(null);

export function CmsContextProvider({
  value, children,
}: { value: CmsContextValue; children: ReactNode }) {
  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

/**
 * Read the context. Throws outside a provider rather than returning null, because a block
 * rendering without its entity is a programming error that should surface in the error
 * boundary rather than produce a silently empty section.
 */
export function useCmsContext(): CmsContextValue {
  const context = useContext(CmsContext);
  if (!context) throw new Error('A CMS block was rendered outside a CmsContextProvider.');
  return context;
}

/** Narrowing helper for the event blocks, which are declared event-context only. */
export function useEventContext(): EventContextValue {
  const context = useCmsContext();
  if (context.kind !== 'event') {
    throw new Error(`This block requires an event context, got "${context.kind}".`);
  }
  return context;
}
